import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeleteTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
  memberCount: number;
  challengeId: string;
  leaderName: string;
  onTeamDeleted: () => void;
}

const DeleteTeamDialog: React.FC<DeleteTeamDialogProps> = ({
  open,
  onOpenChange,
  teamId,
  teamName,
  memberCount,
  challengeId,
  leaderName,
  onTeamDeleted,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setStep(1);
    setConfirmText("");
    onOpenChange(false);
  };

  const handleContinueToStep2 = () => {
    setStep(2);
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;

    setIsDeleting(true);
    try {
      // Get current user ID for the notification
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Soft delete the team by setting deleted_at
      const { error } = await supabase
        .from("challenge_teams")
        .update({
          deleted_at: new Date().toISOString(),
          status: "disbanded",
        })
        .eq("id", teamId);

      if (error) throw error;

      // Send notification to team members (non-blocking)
      if (memberCount > 1) {
        supabase.functions.invoke("send-team-disbanded-notification", {
          body: {
            team_id: teamId,
            challenge_id: challengeId,
            leader_name: leaderName,
            leader_user_id: user.id,
          },
        }).then(({ error: notifError }) => {
          if (notifError) {
            console.error("Failed to send disbanded notifications:", notifError);
          } else {
            console.log("Disbanded notifications sent successfully");
          }
        });
      }

      toast.success("Team has been disbanded");
      handleClose();
      onTeamDeleted();
    } catch (error) {
      console.error("Error deleting team:", error);
      toast.error("Failed to delete team");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-md">
        {step === 1 ? (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <AlertDialogTitle>Delete Team?</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="space-y-4 pt-2">
                  <p>
                    You're about to delete <strong>"{teamName}"</strong>
                  </p>
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                    <p className="font-medium text-destructive">This will:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Remove the team from the challenge</li>
                      <li>
                        Remove all {memberCount} team member{memberCount !== 1 ? "s" : ""} from this challenge
                      </li>
                      <li>Cancel any pending join requests</li>
                      {memberCount > 1 && (
                        <li className="text-primary">Notify other team members via email</li>
                      )}
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The team data will be retained for administrative records.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button variant="destructive" onClick={handleContinueToStep2}>
                Continue to Delete →
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                <AlertDialogTitle>Final Confirmation</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="space-y-4 pt-2">
                  <p>
                    Type <strong>DELETE</strong> to confirm:
                  </p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="font-mono"
                    autoFocus
                  />
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone by you.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>
                ← Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={confirmText !== "DELETE" || isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Team Permanently"}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteTeamDialog;
